/**
 * Per-session Deep Explorer types and helpers. Lives separately from
 * `ai-explorer-store.ts` because `chat-slice.ts` references
 * `DeepExplorerSessionState` on `SessionState` — putting the types here
 * breaks the chat-slice → ai-explorer-store import cycle.
 *
 * External consumers should keep importing from
 * `@/stores/ai-explorer-store`, which re-exports everything here.
 */

/**
 * Wire-aligned with `PlanTodo.status` in `app/backend/approaches/explorerdeep/
 * artifact_models.py` (the canonical schema). The previous local enum used
 * `"done"` while the backend emits `"completed"`; the cast in
 * `use-ai-explorer-chat.ts` masked the mismatch and silently broke
 * strikethrough styling on persisted todos. See QA finding #18.
 */
export type DeepTodoStatus = "pending" | "in_progress" | "completed"

export interface DeepTodo {
  id: string
  /** deepagents uses "content"; legacy code used "text". Both are supported. */
  content?: string
  text?: string
  status: DeepTodoStatus
  /** ISO timestamp; set by backend when status→in_progress */
  started_at?: string | null
  /** ISO timestamp; set by backend when status→done */
  completed_at?: string | null
}

export interface DeepFile {
  path: string
  content: string
  /** Previous content before the last edit — populated by throttler; absent if no edit history */
  previous_content?: string | null
  lastModified: number
}

export interface DeepActivityEvent {
  id: string
  /** `status` events are non-tool updates — replanning, waiting-for-input,
   *  errors, etc. They surface in the lane timeline alongside tool events
   *  so the orchestrator lane has visible data even before the first tool
   *  call fires (planning phase). */
  event: "tool_start" | "tool_end" | "status"
  /** Tool name. Empty string for `status` events. */
  tool: string
  source: string
  /** Short hex (8 chars) identifying a unique subagent dispatch. Lets
   *  the UI lane/colour parallel scout runs. Absent for orchestrator-
   *  level tool calls. */
  dispatch_id?: string | null
  timestamp: string
  input_summary?: Record<string, unknown>
  result_summary?: Record<string, unknown>
}

export interface DeepSource {
  title: string
  snippet?: string
  type: "library" | "web" | "doc" | "m365" | "other"
  tool: string
  timestamp: string
}

export interface QuestionItem {
  id: string
  question: string
  type: "text" | "single_choice" | "multi_choice"
  options?: string[]
  default?: string
}

export interface AskUserPayload {
  ask_id?: string | null
  context: string
  questions: QuestionItem[]
  proposed_todos?: DeepTodo[]
}

/**
 * Per-session run state for Deep Explorer. One slot per `sessionId` lives at
 * `sessionStates[sid].deepExplorerState`. Two background runs and a foreground
 * turn each get their own slot, so WS frames from one session can no longer
 * clobber another's view (Bundle C — fixes Erik's mixed-panel symptom).
 *
 * User preferences (`panelOpen`, `deepModeIntroDismissedWhileIdle`) live at
 * the namespace level on the AI Explorer store — see `ai-explorer-store.ts`.
 */
export interface DeepExplorerSessionState {
  todos: DeepTodo[]
  files: DeepFile[]
  /** Currently visible file path (user-selected or auto-set by agent writes) */
  activeFile: string | null
  /** Last processed snapshot sequence number (for throttle dedup) */
  snapshot_seq: number
  /** Token budget usage from backend */
  token_budget: { used: number; total: number } | null
  /** Session-scoped auto-approve for plan modal — resets on new chat */
  autoApprove: boolean
  /** True once the user has explicitly dismissed the deep panel within the
   *  current chat session. Used so the "open by default in a deep chat" UX
   *  doesn't fight with an explicit close. Reset on session switch. */
  panelDismissedForSession: boolean
  /** True when a plan is waiting for user decision */
  planProposed: boolean
  /** True once the user has approved the current plan — gates thinking vs answer rendering */
  planApproved: boolean
  /** Proposed todos from the plan (before approve/edit/cancel) */
  proposedTodos: DeepTodo[]
  /** UI-5 rewind: snapshot of the plan at approval time, used to restore the
   *  plan editor when the user rewinds a running execution. ``null`` means
   *  nothing to rewind to — populated by the chat hook on the approve path,
   *  cleared on a fresh plan proposal or session reset. */
  planSnapshot: {
    todos: DeepTodo[]
    /** Markdown content of /plan.md at the moment the user approved. */
    planContent: string
    /** ms epoch — surfaced to the rewind tooltip ("approved 2m ago"). */
    takenAt: number
  } | null
  /** True when the agent is waiting for clarification answers */
  askUserProposed: boolean
  /** Payload from the deep_ask_user WS frame */
  askUserPayload: AskUserPayload | null
  /** Name of the currently active subagent, or null if none running */
  subagentActive: string | null
  /** Activity log events from deep_activity_event WS frames */
  activityLog: DeepActivityEvent[]
  /** Accumulated sources from search tool results */
  sources: DeepSource[]
  /** Deliverable format key chosen by the planner (e.g. "research_report",
   *  "memo"). Set by the deep_deliverable_type frame. Null until approved. */
  deliverableType: string | null
  /** Execution mode resolved by the backend on approval — one of
   *  ``reuse_scout`` / ``validate_once`` / ``full_worker_execution``. Null
   *  until the deep_deliverable_type frame lands. The plan dropdown still
   *  parses /plan.md as the ground truth before approval; this field is
   *  the post-approval reflection from the gate middleware. */
  executionMode: string | null
  /** Smart follow-up suggestions emitted after the final answer. Each has
   *  a pre-drafted mini-plan the user can one-click to launch a new turn. */
  followupSuggestions: { question: string; miniPlan: string[] }[]
  /** Citation verifications emitted by the fact-checker subagent.
   *  Map of raw citation string → verdict. */
  citationVerifications: Record<
    string,
    { verified: boolean | null; issue?: string; actualSnippet?: string }
  >
  /** Aggregate counts for the Source Ribbon verification header. */
  verificationSummary: {
    total: number
    verified: number
    unverified: number
  } | null
  /** B7: live subagent reports keyed by `dispatch_id`. Each entry is the
   *  accumulating LLM output stream from a running subagent — surfaced under
   *  the matching `task` tool row in WorkerLanes so users see the agent's
   *  reasoning live, not just the buffered final report. */
  liveSubagentReports: Record<string, string>
  /** B4: live orchestrator reasoning blocks. One entry per
   *  `on_chat_model_start` → `on_chat_model_end` cycle in the orchestrator,
   *  keyed by langchain run_id. While streaming, ``live`` is true and the
   *  block renders with a "Live" pill. On ``_complete`` it flips to false;
   *  on ``_discard`` the entry is dropped (the chunks turned out to be the
   *  final answer prefix, not inter-tool reasoning). Ordering in the
   *  orchestrator lane is by ``startedAt`` so blocks interleave with tool
   *  calls chronologically. */
  orchestratorReasoning: OrchestratorReasoningBlock[]
  /** Round-4: per-subagent-dispatch reasoning blocks. Keyed by
   *  ``dispatch_id``; each subagent invocation accumulates its own
   *  per-run_id reasoning blocks just like the orchestrator does. The
   *  subagent lane in WorkerLanes interleaves these with the subagent's
   *  own tool events. */
  subagentReasoning: Record<string, OrchestratorReasoningBlock[]>
}

export interface OrchestratorReasoningBlock {
  /** langchain run_id of the chat-model invocation. Stable across all
   *  chunk/complete/discard frames for the same block. */
  id: string
  /** Accumulated delta text. */
  text: string
  /** True while the block is still streaming. Flipped to false on
   *  ``deep_orchestrator_reasoning_complete``. */
  live: boolean
  /** Reserved — see DeepOrchestratorReasoningCompleteMessage. Always null
   *  in the v1 backend. */
  followingToolId: string | null
  /** ms epoch — set when the first chunk for this id arrives. Drives the
   *  chronological merge with tool calls in the orchestrator lane. */
  startedAt: number
}

export const defaultDeepExplorerSessionState: DeepExplorerSessionState = {
  todos: [],
  files: [],
  activeFile: null,
  snapshot_seq: 0,
  token_budget: null,
  autoApprove: false,
  panelDismissedForSession: false,
  planProposed: false,
  planApproved: false,
  proposedTodos: [],
  planSnapshot: null,
  askUserProposed: false,
  askUserPayload: null,
  subagentActive: null,
  activityLog: [],
  sources: [],
  deliverableType: null,
  executionMode: null,
  followupSuggestions: [],
  citationVerifications: {},
  verificationSummary: null,
  liveSubagentReports: {},
  orchestratorReasoning: [],
  subagentReasoning: {},
}

/**
 * Read the per-session deep state for a given session id, falling back to
 * the default empty state if the session has no slot yet (e.g. frame
 * arriving before `initSession`, or a consumer reading before any deep
 * activity has happened on the current session).
 */
export function getDeepStateForSession(
  state: { sessionStates: Record<string, { deepExplorerState?: DeepExplorerSessionState }> },
  sessionId: string | null | undefined
): DeepExplorerSessionState {
  if (!sessionId) return defaultDeepExplorerSessionState
  // Record indexing types as non-nullable, but at runtime a missing key is
  // undefined — the optional chain is real protection, not unnecessary.
  const session: { deepExplorerState?: DeepExplorerSessionState } | undefined =
    state.sessionStates[sessionId]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return session?.deepExplorerState ?? defaultDeepExplorerSessionState
}
